import { useState, useEffect, useRef, useCallback } from 'react';
import type React from 'react';
import { fetchWithAuth } from '../lib/api';

interface ChapterMeta {
  chapterIndex: number;
  areaId: string;
  title: string;
  sectionId?: string;
}

interface StudioSidebarProps {
  chapters: ChapterMeta[];
  lockMap: Record<string, boolean>;
  /** sectionId → locked, from the structured book document — drives the lock badge. */
  sectionLocks?: Record<string, boolean>;
  restUrl: string;
  onLockMapChange: (lockMap: Record<string, boolean>) => void;
  contentPaneId: string;
  onReorderChapters?: (chapters: ChapterMeta[]) => void;
  /** Ref populated by this component so callers can trigger a per-chapter 409 toast. */
  showSectionLockToastRef?: React.MutableRefObject<((sectionId: string, message: string) => void) | null>;
  /** Starts a single-chapter regeneration job (optional author instruction). */
  onRegenerateChapter?: (sectionId: string, instruction: string) => void;
  coverKey?: string | null;
  onCoverSectionClick?: () => void;
  activeSection?: 'cover' | 'chapter' | null;
  // TOC/Index generation (Phase 71)
  onGenerateToc?: () => void;
  onGenerateIndex?: () => void;
  tocGenerating?: boolean;
  indexGenerating?: boolean;
  tocStale?: boolean;
  indexStale?: boolean;
}

export function StudioSidebar({
  chapters,
  lockMap,
  sectionLocks,
  restUrl,
  onLockMapChange,
  contentPaneId,
  onReorderChapters,
  showSectionLockToastRef,
  onRegenerateChapter,
  coverKey,
  onCoverSectionClick,
  activeSection,
  onGenerateToc,
  onGenerateIndex,
  tocGenerating = false,
  indexGenerating = false,
  tocStale = false,
  indexStale = false,
}: StudioSidebarProps) {
  const [activeChapterIndex, setActiveChapterIndex] = useState<number | null>(null);
  const [lockToast, setLockToast] = useState<string | null>(null);
  const lockToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sectionLockToasts, setSectionLockToasts] = useState<Record<string, string | null>>({});
  const sectionLockToastTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [regenTargetId, setRegenTargetId] = useState<string | null>(null);
  const [regenInstruction, setRegenInstruction] = useState('');

  const showSectionLockToast = useCallback((sectionId: string, message: string) => {
    const existing = sectionLockToastTimers.current.get(sectionId);
    if (existing) clearTimeout(existing);
    setSectionLockToasts(prev => ({ ...prev, [sectionId]: message }));
    const timer = setTimeout(() => {
      setSectionLockToasts(prev => ({ ...prev, [sectionId]: null }));
      sectionLockToastTimers.current.delete(sectionId);
    }, 4000);
    sectionLockToastTimers.current.set(sectionId, timer);
  }, []);

  // Populate the ref so callers (BookStudio → Book.tsx) can trigger toasts
  useEffect(() => {
    if (showSectionLockToastRef) {
      showSectionLockToastRef.current = showSectionLockToast;
    }
    return () => {
      if (showSectionLockToastRef) showSectionLockToastRef.current = null;
    };
  }, [showSectionLockToastRef, showSectionLockToast]);

  // Cleanup all section lock toast timers on unmount
  useEffect(() => {
    return () => {
      sectionLockToastTimers.current.forEach(timer => clearTimeout(timer));
      sectionLockToastTimers.current.clear();
    };
  }, []);

  // IntersectionObserver to track active chapter
  useEffect(() => {
    if (chapters.length === 0) return;

    const root = document.getElementById(contentPaneId);
    if (!root) return;

    const headings = chapters.map(ch => {
      const id =
        ch.areaId !== '' ? `chapter-${ch.chapterIndex + 1}` :
        ch.chapterIndex === -3 ? 'section-title-page' :
        ch.chapterIndex === -2 ? 'section-dedication-page' :
        ch.chapterIndex === -1 ? 'section-toc-page' :
        ch.chapterIndex === 998 ? 'section-back-matter' :
        ch.chapterIndex === 999 ? 'section-index-page' :
        '';
      return id ? root.querySelector<HTMLElement>(`[id="${id}"]`) : null;
    }).filter(Boolean) as HTMLElement[];

    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          const topEntry = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          const id = topEntry.target.getAttribute('id');
          if (id) {
            if (id.startsWith('chapter-')) {
              const n = Number(id.replace('chapter-', '')) - 1;
              setActiveChapterIndex(n);
            } else if (id === 'section-title-page')      setActiveChapterIndex(-3);
            else if (id === 'section-dedication-page')   setActiveChapterIndex(-2);
            else if (id === 'section-toc-page')          setActiveChapterIndex(-1);
            else if (id === 'section-back-matter')       setActiveChapterIndex(998);
            else if (id === 'section-index-page')        setActiveChapterIndex(999);
          }
        }
      },
      { root, threshold: 0.3 }
    );

    headings.forEach(h => observer.observe(h));

    return () => observer.disconnect();
  }, [chapters, contentPaneId]);

  const toggleLock = async (areaId: string) => {
    const willLock = !lockMap[areaId];
    onLockMapChange({ ...lockMap, [areaId]: willLock });

    if (lockToastTimerRef.current) clearTimeout(lockToastTimerRef.current);
    setLockToast(willLock ? 'Chapter locked' : 'Chapter unlocked');
    lockToastTimerRef.current = setTimeout(() => setLockToast(null), 3000);

    try {
      const res = await fetchWithAuth(
        `${restUrl}chapters/${encodeURIComponent(areaId)}/lock`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locked: willLock }),
        }
      );
      if (!res.ok) throw new Error('Lock failed');
    } catch {
      onLockMapChange({ ...lockMap, [areaId]: !willLock }); // revert
      if (lockToastTimerRef.current) clearTimeout(lockToastTimerRef.current);
      setLockToast('Failed to update lock — try again');
      lockToastTimerRef.current = setTimeout(() => setLockToast(null), 4000);
    }
  };

  const toggleSectionLock = async (sectionId: string, currentlyLocked: boolean) => {
    const action = currentlyLocked ? 'unlock' : 'lock';
    try {
      const res = await fetchWithAuth(`${restUrl}book/section/${sectionId}/${action}`, { method: 'POST' });
      if (!res.ok) throw new Error('Lock failed');
      // Update lockMap using sectionId as key so StudioContent can read it
      onLockMapChange({ ...lockMap, [sectionId]: !currentlyLocked });
    } catch {
      if (lockToastTimerRef.current) clearTimeout(lockToastTimerRef.current);
      setLockToast('Failed to update lock — try again');
      lockToastTimerRef.current = setTimeout(() => setLockToast(null), 4000);
    }
  };

  // Real chapters only (exclude front/back matter synthetic entries from Plan 2).
  const realChapters = chapters.filter(c => c.areaId !== '');

  const moveChapter = (chapter: ChapterMeta, direction: 'up' | 'down') => {
    if (!onReorderChapters) return;
    const idx = realChapters.findIndex(c => c.areaId === chapter.areaId);
    if (idx === -1) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= realChapters.length) return;
    const next = [...realChapters];
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    onReorderChapters(next);
  };

  return (
    <nav className="flex flex-col h-full" aria-label="Chapter navigation">
      {/* Cover entry — above all chapters */}
      <div className="px-2 pt-2 pb-1 flex-shrink-0">
        <button
          type="button"
          onClick={onCoverSectionClick}
          className={`w-full text-left px-3 py-2 rounded text-sm font-medium flex items-center gap-2 ${activeSection === 'cover' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          <span>Cover</span>
          {coverKey && <span className="ml-auto text-xs text-green-600">✓</span>}
        </button>
      </div>

      <div className="px-4 py-3 border-b border-stone-100 flex-shrink-0">
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Chapters</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {chapters.length === 0 ? (
          <p className="text-sm text-stone-400 px-4 py-3">No chapters found.</p>
        ) : (
          chapters.map(ch => {
            const targetId =
              ch.areaId !== '' ? `chapter-${ch.chapterIndex + 1}` :
              ch.chapterIndex === -3 ? 'section-title-page' :
              ch.chapterIndex === -2 ? 'section-dedication-page' :
              ch.chapterIndex === -1 ? 'section-toc-page' :
              ch.chapterIndex === 998 ? 'section-back-matter' :
              ch.chapterIndex === 999 ? 'section-index-page' :
              '';

            const scrollToSection = () => {
              if (!targetId) return;
              document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };

            return (
              <div key={`${ch.chapterIndex}-${ch.areaId}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={scrollToSection}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') scrollToSection();
                  }}
                  className={[
                    'flex items-center gap-2 px-4 cursor-pointer select-none transition-colors',
                    activeChapterIndex === ch.chapterIndex
                      ? 'bg-amber-50 border-l-2 border-amber-500'
                      : lockMap[ch.areaId]
                      ? 'bg-stone-50 border-l-2 border-transparent hover:bg-stone-100'
                      : 'border-l-2 border-transparent hover:bg-stone-50',
                  ].join(' ')}
                  style={{ minHeight: '44px', paddingTop: '12px', paddingBottom: '12px' }}
                >
                  <span
                    className={`flex-1 text-sm truncate ${
                      lockMap[ch.areaId] ? 'text-stone-500 font-medium' : 'text-stone-800 font-semibold'
                    }`}
                  >
                    {ch.title}
                  </span>
                  {/* Lock badge from book.json section data — chapter will be preserved on regenerate */}
                  {ch.areaId !== '' && ch.sectionId && sectionLocks?.[ch.sectionId] && !lockMap[ch.areaId] && (
                    <span
                      className="flex-shrink-0 text-amber-600"
                      title="Chapter locked — preserved during regeneration"
                      aria-label="Chapter locked"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                  {ch.areaId !== '' && (
                    <>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); moveChapter(ch, 'up'); }}
                        disabled={realChapters.findIndex(c => c.areaId === ch.areaId) === 0}
                        className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                        aria-label={`Move ${ch.title} up`}
                        title="Move chapter up"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); moveChapter(ch, 'down'); }}
                        disabled={realChapters.findIndex(c => c.areaId === ch.areaId) === realChapters.length - 1}
                        className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                        aria-label={`Move ${ch.title} down`}
                        title="Move chapter down"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </>
                  )}
                  {/* Per-chapter regenerate action */}
                  {ch.areaId !== '' && ch.sectionId && onRegenerateChapter && (() => {
                    const chapterLocked = !!(
                      lockMap[ch.areaId] || lockMap[ch.sectionId!] || sectionLocks?.[ch.sectionId!]
                    );
                    return (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          if (chapterLocked) return;
                          setRegenInstruction('');
                          setRegenTargetId(prev => (prev === ch.sectionId ? null : ch.sectionId!));
                        }}
                        disabled={chapterLocked}
                        className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                        aria-label={chapterLocked ? 'Unlock to regenerate' : `Regenerate ${ch.title}`}
                        title={chapterLocked ? 'Unlock to regenerate' : 'Regenerate chapter'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                    );
                  })()}
                  {/* Real chapter lock toggle */}
                  {ch.areaId !== '' && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); void toggleLock(ch.areaId); }}
                      className="p-1 text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0"
                      aria-label={
                        lockMap[ch.areaId]
                          ? 'Click to unlock chapter'
                          : 'Lock chapter to protect from AI edits'
                      }
                      title={
                        lockMap[ch.areaId]
                          ? 'Click to unlock chapter'
                          : 'Lock chapter to protect from AI edits'
                      }
                    >
                      {lockMap[ch.areaId] ? (
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                  {/* Title-page: always locked — static icon */}
                  {ch.areaId === '' && ch.chapterIndex === -3 && (
                    <span className="p-1 text-amber-600 flex-shrink-0" title="Title page is always included">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                  {/* TOC: stale badge + generate button */}
                  {ch.areaId === '' && ch.chapterIndex === -1 && (
                    <>
                      {tocStale && !tocGenerating && (
                        <span
                          className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"
                          title="TOC may be out of date — regenerate to update page numbers"
                          aria-label="TOC stale"
                        />
                      )}
                      {tocGenerating ? (
                        <svg className="animate-spin h-4 w-4 text-amber-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      ) : (
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); onGenerateToc?.(); }}
                          className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded transition-colors flex-shrink-0"
                          aria-label="Generate Table of Contents"
                          title="Generate Table of Contents from chapter page numbers"
                        >
                          {tocStale ? 'Refresh TOC' : 'Generate TOC'}
                        </button>
                      )}
                    </>
                  )}
                  {/* Index: stale badge + generate button */}
                  {ch.areaId === '' && ch.chapterIndex === 999 && (
                    <>
                      {indexStale && !indexGenerating && (
                        <span
                          className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"
                          title="Index may be out of date — regenerate to update entries"
                          aria-label="Index stale"
                        />
                      )}
                      {indexGenerating ? (
                        <svg className="animate-spin h-4 w-4 text-amber-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      ) : (
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); onGenerateIndex?.(); }}
                          className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded transition-colors flex-shrink-0"
                          aria-label="Generate Index"
                          title="Generate Index from chapter content using AI"
                        >
                          {indexStale ? 'Refresh Index' : 'Generate Index'}
                        </button>
                      )}
                    </>
                  )}
                  {/* Dedication / back-matter: interactive lock toggle via section API */}
                  {ch.areaId === '' && (ch.chapterIndex === -2 || ch.chapterIndex === 998) && ch.sectionId && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); void toggleSectionLock(ch.sectionId!, !!lockMap[ch.sectionId!]); }}
                      className="p-1 text-stone-400 hover:text-stone-600 transition-colors flex-shrink-0"
                      aria-label={
                        lockMap[ch.sectionId]
                          ? 'Click to unlock section'
                          : 'Lock section to protect from AI edits'
                      }
                      title={
                        lockMap[ch.sectionId]
                          ? 'Click to unlock section'
                          : 'Lock section to protect from AI edits'
                      }
                    >
                      {lockMap[ch.sectionId] ? (
                        <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
                {ch.sectionId && sectionLockToasts[ch.sectionId] && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 mx-4">
                    {sectionLockToasts[ch.sectionId]}
                  </p>
                )}
                {/* Regenerate confirm panel */}
                {ch.sectionId && regenTargetId === ch.sectionId && (
                  <div className="mx-4 mt-1 mb-2 p-3 border border-stone-200 rounded-lg bg-stone-50 space-y-2">
                    <p className="text-xs text-stone-600">
                      Rewrite "{ch.title}" from your recorded memories? Other chapters stay untouched.
                    </p>
                    <input
                      type="text"
                      value={regenInstruction}
                      onChange={e => setRegenInstruction(e.target.value)}
                      maxLength={500}
                      placeholder="Optional: tell the AI how to change it"
                      className="w-full text-xs border border-stone-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-500"
                      aria-label="Optional direction for the AI"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          onRegenerateChapter?.(ch.sectionId!, regenInstruction.trim());
                          setRegenTargetId(null);
                        }}
                        className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded font-semibold transition-colors"
                      >
                        Regenerate
                      </button>
                      <button
                        type="button"
                        onClick={() => setRegenTargetId(null)}
                        className="text-xs text-stone-500 hover:text-stone-700 px-2 py-1.5 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Lock toast */}
      {lockToast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50"
        >
          {lockToast}
        </div>
      )}
    </nav>
  );
}
